// Angular modules.
import {
	AfterViewInit,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
// Third-party modules.
import { TranslateService } from '@ngx-translate/core';
import { ZkNotificationService } from '@zenklub/ng-ui';
// Zenklub interfaces.
import {
	IAvailability,
	IAvailabilityApi,
	IAvailabilityPeriod,
	IWeekdaySlot,
	IWeekdaySlotItem,
} from 'app/interfaces/availability.interface';
import { IUserPreferences } from 'app/interfaces/userpreferences.interface';
// Zenklub services.
import { AvailabilitiesService } from 'app/services/availabilities.service';
import { DateTimeService } from 'app/services/datetime.service';
import { UserService } from 'app/services/user.service';
import * as moment from 'moment-timezone';
@Component({
	selector: 'app-mobavailabilities-edit',
	templateUrl: './mobavailabilities-edit.component.html',
	styleUrls: ['./mobavailabilities-edit.component.scss'],
})
export class MobAvailabilitiesEditComponent
	implements AfterViewInit, OnChanges, OnInit
{
	// Inputs.
	@Input() currentTimezone: string;
	@Input() newPeriodDate: Date[];
	@Input() newPeriodDateFrom: Date;
	@Input() newPeriodDateTo: Date;
	@Input() availability: IAvailabilityApi;
	maxInputDate = new Date();
	// Outputs.
	@Output() cancelEdit: EventEmitter<any> = new EventEmitter<any>();
	// Status
	isLoading = false;
	// User's preferences.
	userPref: IUserPreferences;
	// Calendar data.
	calendarLabels: any;
	availableDays: number[] = [];
	slots: IAvailability;
	hoursList: string[] = this.dateTime.buildDailyHours(true);
	// Strings.
	strAreYouSure: string;
	strToDateMustBeGreater: string;
	// Constructor.
	constructor(
		public notifications: ZkNotificationService,
		protected availabilities: AvailabilitiesService,
		protected dateTime: DateTimeService,
		protected route: ActivatedRoute,
		protected router: Router,
		protected translate: TranslateService,
		protected userService: UserService
	) {}

	// On init.
	public ngOnInit(): void {
		// Is loading.
		this.isLoading = true;

		this.newPeriodDate = [this.newPeriodDateFrom, this.newPeriodDateTo];
		// Update dates interval.
		this.newPeriodDate[0] = moment
			.parseZone(this.availability.startDate)
			.toDate();
		this.newPeriodDate[1] = moment
			.parseZone(this.availability.endDate)
			.toDate();
		// set max date
		const now = new Date();
		this.maxInputDate = new Date(
			now.getFullYear(),
			now.getMonth() + 3,
			now.getDate()
		);
		// Get user preferences.
		this.userPref = this.userService.getUserLocalPreferences();
		// Determine calendar modes and labels.
		this.translate
			.get([
				'CALENDAR.MODES.MONTH',
				'CALENDAR.MODES.WEEK',
				'CALENDAR.MODES.DAY',
				'CALENDAR.MONTHS',
				'CALENDAR.WEEKDAYS',
				'MESSAGES.AREYOUSURE',
				'MESSAGES.TODATEMUSTBEGREATER',
			])
			.subscribe((res) => {
				// Calendar labels.
				const cMon: any = res['CALENDAR.MONTHS'];
				const cWee: any = res['CALENDAR.WEEKDAYS'];
				this.calendarLabels = {
					firstDayOfWeek: 0,
					dayNames: [
						cWee.SUN.SHORT,
						cWee.MON.SHORT,
						cWee.TUE.SHORT,
						cWee.WED.SHORT,
						cWee.THU.SHORT,
						cWee.FRI.SHORT,
						cWee.SAT.SHORT,
					],
					dayNamesShort: [
						cWee.SUN.ABB,
						cWee.MON.ABB,
						cWee.TUE.ABB,
						cWee.WED.ABB,
						cWee.THU.ABB,
						cWee.FRI.ABB,
						cWee.SAT.ABB,
					],
					monthNames: [
						cMon.JAN.FULL,
						cMon.FEB.FULL,
						cMon.MAR.FULL,
						cMon.APR.FULL,
						cMon.MAY.FULL,
						cMon.JUN.FULL,
						cMon.JUL.FULL,
						cMon.AUG.FULL,
						cMon.SEP.FULL,
						cMon.OCT.FULL,
						cMon.NOV.FULL,
						cMon.DEC.FULL,
					],
					monthNamesShort: [
						cMon.JAN.ABB,
						cMon.FEB.ABB,
						cMon.MAR.ABB,
						cMon.APR.ABB,
						cMon.MAY.ABB,
						cMon.JUN.ABB,
						cMon.JUL.ABB,
						cMon.AUG.ABB,
						cMon.SEP.ABB,
						cMon.OCT.ABB,
						cMon.NOV.ABB,
						cMon.DEC.ABB,
					],
				};
				// Strings.
				this.strAreYouSure = res['MESSAGES.AREYOUSURE'];
				this.strToDateMustBeGreater = res['MESSAGES.TODATEMUSTBEGREATER'];
				// Update available weekdays.
				this.updateAvailableWeekdays();
				// Build slots with saved data.
				this.buildSavedSlots(0);
			});
	}

	// On changes.
	public ngOnChanges(): void {
		// Resize and organize elements.
		this.onResize();
	}

	// After view init.
	public ngAfterViewInit(): void {
		// Resize and organize elements.
		this.onResize();
	}

	// Update available weekdays.
	public updateAvailableWeekdays(): void {
		this.availableDays = [1, 2, 3, 4, 5, 6, 0];
	}

	// Build slots with saved data.
	private buildSavedSlots(index: number): void {
		const len: number = this.availability.periods.length;
		if (len > 0 && len - 1 >= index) {
			const ts: IAvailabilityPeriod = this.availability.periods[index];
			const arStart: string[] = ts.startTime.split(':');
			const arEnd: string[] = ts.endTime.split(':');
			const startHour: number = parseInt(arStart[0], 10);
			const startMin: number = parseInt(arStart[1], 10);
			const endHour: number = parseInt(arEnd[0], 10);
			const endMin: number = parseInt(arEnd[1], 10);
			const weekDay: number = ts.dayOfWeek;
			this.addDayBlock(startHour, startMin, endHour, endMin, weekDay).then(
				() => {
					++index;
					this.buildSavedSlots(index);
				}
			);
		} else {
			// Loaded.
			this.isLoading = false;
		}
	}

	// Add a new slot for a day.
	public addDayBlock(
		startHours = 8,
		startMinutes = 0,
		endHours = 9,
		endMinutes = 0,
		dayOfWeek?: number
	): Promise<any> {
		const promise = new Promise<void>((resolve) => {
			// Ensure weekday.
			if (dayOfWeek === null || dayOfWeek === undefined) {
				dayOfWeek = this.availableDays.length > 0 ? this.availableDays[0] : 1;
			}
			// Remove weekday from available days list.
			this.availableDays.forEach((day, index) => {
				if (day === dayOfWeek) {
					this.availableDays.splice(index, 1);
				}
			});
			// Create new slot.
			const wdsItem: IWeekdaySlotItem = this.availabilities.createSlotItem(
				startHours,
				startMinutes,
				endHours,
				endMinutes
			);
			const tSlot: IWeekdaySlot = this.availabilities.createWeekdaySlot(
				dayOfWeek,
				[wdsItem]
			);
			if (this.slots) {
				this.slots.weekdaySlots.push(tSlot);
			} else {
				const sDate: string = this.dateTime.getSimpleDate(
					this.newPeriodDate[0],
					'-'
				);
				const eDate: string = this.dateTime.getSimpleDate(
					this.newPeriodDate[1],
					'-'
				);
				this.slots = {
					startDate: sDate,
					endDate: eDate,
					weekdaySlots: [tSlot],
					key:
						'ava.' +
						Math.floor(Math.random() * 1000).toString() +
						'.' +
						Date.now().toFixed(),
				};
			}
			// Configure elements position and size.
			this.onResize('addDaysContainer');
			// Resolve.
			resolve();
		});
		return promise;
	}

	// Add an hour interval.
	addHourInterval(key: string): void {
		this.slots.weekdaySlots.forEach((slot, index) => {
			// Next start hours.
			let nextHours = 8;
			let nextMinutes = 0;
			// Find related weekday.
			if (slot.key === key) {
				// Get hour of sequence.
				if (slot.items.length > 0) {
					const lastItem: IWeekdaySlotItem = slot.items[slot.items.length - 1];
					nextHours = lastItem.endHours;
					nextMinutes = lastItem.endMinutes;
				}
				// Next end hours.
				const nextEndHours: number = nextHours < 23 ? nextHours + 1 : 0;
				const nextEndMinutes: number = nextMinutes;
				// New timeslot.
				const newSlotItem: IWeekdaySlotItem = {
					startHours: nextHours,
					startMinutes: nextMinutes,
					endHours: nextEndHours,
					endMinutes: nextEndMinutes,
					startString: this.dateTime.getHourString(nextHours, nextMinutes),
					endString: this.dateTime.getHourString(nextEndHours, nextEndMinutes),
					key:
						'slo.' +
						Math.floor(Math.random() * 100).toString() +
						'.' +
						Date.now().toFixed(),
				};
				// Append new slot.
				this.slots.weekdaySlots[index].items.push(newSlotItem);
			}
		});
	}

	// Remove an hour interval.
	removeHourInterval(item: string, key: string): void {
		this.slots.weekdaySlots.forEach((slot, index) => {
			if (slot.key === item) {
				slot.items.forEach((slotItem, i) => {
					if (slotItem.key === key) {
						this.slots.weekdaySlots[index].items.splice(i, 1);
					}
				});
				if (slot.items.length === 0) {
					this.slots.weekdaySlots.splice(index, 1);
				}
			}
		});
		if (this.slots.weekdaySlots.length === 0) {
			this.updateAvailableWeekdays();
		}
	}

	// When an hour is selected.
	fromHourSelected(parentKey: string, key: string): void {
		// Get selects.
		const selectFrom: JQuery<HTMLElement> = $(
			'div.divTimeTableItem[data-key="' + key + '"] select.selHourSlotFrom'
		);
		const selectTo: JQuery<HTMLElement> = $(
			'div.divTimeTableItem[data-key="' + key + '"] select.selHourSlotTo'
		);
		// Selected from hour.
		const hourFrom: string = selectFrom.val().toString();
		const hourTo: string = selectTo.val().toString();
		const hF: number = parseInt(hourFrom.split(':')[0], 10);
		const mF: number = parseInt(hourFrom.split(':')[1], 10);
		let hT: number = parseInt(hourTo.split(':')[0], 10);
		let mT: number = parseInt(hourTo.split(':')[1], 10);

		if (hourFrom >= hourTo && hF + mF !== 0) {
			hT = hF < 23 ? hF + 1 : 0;
			mT = mF;
		}

		this.slots.weekdaySlots.forEach((slot) => {
			if (slot.key === parentKey) {
				slot.items.forEach((item) => {
					if (item.key === key) {
						item.startHours = hF;
						item.startMinutes = mF;
						item.endHours = hT;
						item.endMinutes = mT;
						item.startString = this.dateTime.getHourString(hF, mF);
						item.endString = this.dateTime.getHourString(hT, mT);
						selectFrom.val(item.startString);
						selectTo.val(item.endString);
					}
				});
			}
		});
	}

	// When user select a different weekday.
	changeSelectedWeekday(key: string): void {
		// Get select changed.
		const select: JQuery<HTMLElement> = $(
			'.tdTimetables[data-key="' + key + '"] select.selWeekday'
		);
		// New weekday.
		const newWeekday: number = parseInt(select.val().toString(), 10);
		// Change weekday.
		this.slots.weekdaySlots.forEach((oldSlot) => {
			if (oldSlot.key === key) {
				oldSlot.dayOfWeek = newWeekday;
			}
		});
	}

	// Cancel edit.
	public cancelAddNew(): void {
		if (confirm(this.translate.instant('MESSAGES.CONFIRMCANCELEDIT'))) {
			this.cancelEdit.emit();
		}
	}

	// Save data.
	public save(): void {
		// Update dates.
		this.slots.startDate = moment
			.parseZone(this.newPeriodDate[0])
			.utc()
			.startOf('day')
			.toJSON();
		this.slots.endDate = moment
			.parseZone(this.newPeriodDate[1])
			.utc()
			.startOf('day')
			.toJSON();
		// Bind ID.
		this.slots.id = this.availability.id;
		// Try to update.
		this.isLoading = true;
		this.translate
			.get(['MESSAGES.SAVEDSUCCESSFULLY', 'MESSAGES.SAVEDERROR'])
			.subscribe((res) => {
				this.availabilities.edit(this.slots).subscribe(
					() => {
						this.isLoading = false;
						this.notifications.showSuccess(res['MESSAGES.SAVEDSUCCESSFULLY']);
						this.cancelEdit.emit();
					},
					(e) => {
						this.isLoading = false;
						this.notifications.showError(
							e.error?.messageI18n || '',
							res['MESSAGES.SAVEDERROR']
						);
					}
				);
			});
	}

	// // When window is resized.
	public onResize(container = 'apDetails'): void {
		if ($('.' + container).length > 0) {
			// Form elements.
			const formContainer: JQuery<HTMLElement> = $('.apDetails');
			// Get reference container width.
			const contW: number = formContainer.width();
			// Configure select field.
			const fcSelect: JQuery<HTMLElement> = $('.fcSelect');
			const selField: JQuery<HTMLElement> = $('.divSelField');
			const select: JQuery<HTMLElement> = $('.divSelField select');
			if (fcSelect.length > 0 && select.length > 0) {
				selField.css('width', contW - 20);
				const sfW: number = selField.width();
				select.css('width', sfW);
			}
			// Configure calendar selects.
			const tdCalTable: JQuery<HTMLElement> = $('.dtTable');
			const tdCalLabel: JQuery<HTMLElement> = $('.tdCalendarLabel');
			const tdCalInput: JQuery<HTMLElement> = $('.tdCalendarInput');
			const tdCalDtFrom: JQuery<HTMLElement> = $('#inpDtFrom');
			const tdCalDtTo: JQuery<HTMLElement> = $('#inpDtTo');
			const tdWidth: number = tdCalTable.width() - tdCalLabel.width();
			tdCalInput.css('width', tdWidth);
			tdCalDtFrom.css('width', tdWidth);
			tdCalDtTo.css('width', tdWidth);
			// Configure calendar icons.
			const tdCalFrom: JQuery<HTMLElement> = $('.tdCalFrom');
			const tdCalTo: JQuery<HTMLElement> = $('.tdCalTo');
			const iconCalFrom: JQuery<HTMLElement> = $('.iconCalFrom');
			const iconCalTo: JQuery<HTMLElement> = $('.iconCalTo');
			if (iconCalFrom.length > 0) {
				iconCalFrom.css('top', tdCalFrom.position().top + 12);
				iconCalTo.css('top', tdCalTo.position().top + 12);
			}
			// Hide calendar icons.
			$('.owl-dateTime-cancel').hide();
		} else {
			setTimeout(() => {
				this.onResize(container);
			}, 100);
		}
	}
}
